import { CMSController, CMSDownloadPack, ContentPostContentLocationDownloadDto, ControllerHelper, FileEntryContentDownloadDto, FileEntryDownloadDto, ProjectGroupDownloadDto } from 'collaboration-service';
import { FileEntrySearchResultDto } from 'collaboration-service/dist/dtos/FileEntrySearchResultDto';
import { ProjectGroupSearch } from 'collaboration-service/dist/dtos/ProjectGroupSearch';
import { SearchResultsDto } from 'collaboration-service/dist/dtos/SearchResultsDto';
import { GeneralSC as G } from "components/General/GeneralSC";
import { Button, Dimmer, Divider, Icon, Loader, LoaderInline, Theme, Tooltip, useBelowBreakpointOrEqual, useThemePart } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { Provider, useSelector } from 'react-redux';
import { Actions } from 'services/ApplicationState/Actions';
import { ApplicationState, ProjectsFilesCount, useAppDispatch } from 'services/ApplicationState/ApplicationState';
import { shallowCompare } from 'services/Helpers';
import { useCheckWhereIAm } from 'services/Helpers/RoutingHelper';
import { getCMSContent } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import CMSPreview from './CMSPreview';
import CMSProjectInfo from './CMSProjectInfo';
import { CMSSC as T } from './CMSSC';
import useCMSSearch, { useCMSSearchProps, useCMSSearchSelectButtonProps } from './useCMSSearch/useCMSSearch';
import { UseCMSSearchHelpers, adminFileColumns, adminProjectColumns, useCMSSearchDataFromRoute, userFileColumns, userProjectColumns } from "./useCMSSearch/useCMSSearchHelpers";
import { useCMSSearchContext, useCMSSearchStore } from './useCMSSearch/useCMSSearchStateHandling';
import History from 'services/History';



export interface CMSSearchSelectFileButtonProps<T> extends Omit<useCMSSearchSelectButtonProps<T>, "onClick"> {
    onClick: (data: T, markedProjectGroups: ProjectGroupDownloadDto[], markedFileEntries: FileEntryDownloadDto[]) => void;
}
interface CMSSearchProps {
    content?: boolean;
    onFileClicked?: useCMSSearchProps<{ id: string }, any>["onFileClicked"];
    onProjectClicked?: useCMSSearchProps<{ id: string }, any>["onProjectClicked"];
    onDownload?: (packs: CMSDownloadPack[]) => void;
    onChangeLoading?: (loading: boolean) => void;
    disableSearchResults?: boolean;
    dontUseRouteForState?: useCMSSearchProps<{ id: string }, any>["dontUseRouteForState"];
    renderSearchResults?: useCMSSearchProps<{ id: string }, any>["renderSearchResults"]
    loading?: boolean;
    dontResetSearchResults?: useCMSSearchProps<{ id: string }, any>["dontResetSearchResults"];
    renderHeader?: (callDownload?: () => void) => JSX.Element;
    tableView: boolean;
}

export const RenderFileEntryPreview = (p: { data: FileEntrySearchResultDto, lng: string, location?: ContentPostContentLocationDownloadDto }) => {
    const { data, lng, location } = p;
    const [loadedData, setLoadedData] = React.useState<FileEntrySearchResultDto>();

    //console.log("renderFileEntryPreview::data", data);

    React.useEffect(() => {
        const l = async () => {
            const newData = await ControllerHelper.singleCall({ toFill: data }, CMSController.FillFileSearchResult, true);
            if (newData)
                setLoadedData(newData);
            else
                setLoadedData(data);
        };
        l();
    }, [data])
    const links = loadedData?.links;
    const fe = React.useMemo(() => {
        if (links) {
            const fe: FileEntryDownloadDto = { contents: {} } as any;
            _.forEach(links, (c, l) => {
                const cur: FileEntryContentDownloadDto = {
                    contents: []
                };
                fe.contents[l] = cur;
                _.forEach(c, (m, lng) => {
                    cur.contents!.push({ ...m, lng });
                });
            });
            return getCMSContent({ fileEntry: fe, language: lng, filePosts: {}, location });
        }
        return undefined;

    }, [links, lng, location]);

    if (fe)
        return <CMSPreview
            name={fe?.media?.fileName ?? ""}
            media={fe?.media}
        />;
    else
        return <Loader light size='small' active />
}

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage,
    location: s.cmsState.location,
    searchResultsProjects: s.cmsState.searchResultsProjects,
    contentSearchResultsProjects: s.cmsState.contentSearchResultsProjects,
    params: s.params,
});

const CMSSearch = (p: CMSSearchProps) => {
    const { content, onChangeLoading, disableSearchResults, onFileClicked,
        onProjectClicked, onDownload, dontUseRouteForState, renderSearchResults,
        loading: givenLoading, dontResetSearchResults, renderHeader } = p;
    const { t, currentLanguage } = useImgI18N("cms");
    const { contentLanguage, location, searchResultsProjects, contentSearchResultsProjects } = useSelector(mapper, shallowCompare);
    const isMobile = useBelowBreakpointOrEqual("largeMobile");
    const [showPreview, setShowPreview] = React.useState<FileEntrySearchResultDto>();
    const lng = contentLanguage ?? currentLanguage;
    const dispatch = useAppDispatch();
    const [loadingPage, setLoadingPage] = React.useState<boolean>(false);
    const [packs, setPacks] = React.useState<CMSDownloadPack[] | undefined>();
    const sr = content ? contentSearchResultsProjects?.results : searchResultsProjects?.results;
    const check = useCheckWhereIAm();
    const contentWidth = useThemePart((t: Theme) => t.content.width);
    const open = React.useRef<boolean | undefined>(false);

    React.useEffect(() => {
        //console.log("run...");
        const hashEnd = window.location.hash.indexOf("?");
        const s = window.location.hash.substring(hashEnd);
        const query = new URLSearchParams(s);
        open.current = query.get("open") === "true";
    });

    React.useEffect(() => {
        if (sr) {
            const l = async () => {
                const res = await ControllerHelper.singleCall({ byFileName: false, admin: check("cms"), feFilter: {}, filter: { onlyInProjectGroups: _.map(sr, s => s.id) }, lng: "en-GB", text: "*" },
                    CMSController.SearchFiles, true);
                const toSet: ProjectsFilesCount = {};
                _.forEach((res.facets ?? {})["group_id"], x => {
                    toSet[x.data] = x.count;
                });
                dispatch(Actions.addCMSProjectsFilesCount(toSet));
            }
            l();
        }
    }, [sr, check, dispatch]);

    const routeData = useCMSSearchDataFromRoute();
    const searchTerm = routeData.text;
    const type = routeData.type ?? "files";
    const ordered = routeData.ordered ?? false;
    const props = React.useMemo(() => {
        const sd = type === "files" ? { admin: !content, byFileName: false, maxCount: 12 } : { admin: !content, maxCount: 12, ordered };
        const refine = type === "files" ?
            async (res: SearchResultsDto<FileEntrySearchResultDto>) => {
                //                console.log("CMSSearch.tsx::178 => res", res);
                const p = _.map(res?.results, async r => await ControllerHelper.singleCall({ toFill: r }, CMSController.FillFileSearchResult, true) ?? r);
                const results = await Promise.all(p);
                return { ...res, results };
            } :
            async (res: SearchResultsDto<ProjectGroupSearch>) => {
                //                console.log("CMSSearch.tsx::178 => res", res);
                const p = _.map(res?.results, async r => await ControllerHelper.singleCall({ toFill: r }, CMSController.FillProjectSearchResult, true) ?? r);
                const results = await Promise.all(p);
                return { ...res, results };
            }
        const base = (type === "files" ? (content ? UseCMSSearchHelpers.user.forFiles : UseCMSSearchHelpers.admin.forFiles) :
            (content ? UseCMSSearchHelpers.user.forProjects : UseCMSSearchHelpers.admin.forProjects));
        const columns = type === "files" ? (content ? userFileColumns : adminFileColumns) : (content ? userProjectColumns : adminProjectColumns);
        const renderSubComponent: useCMSSearchProps<any, any>["renderSubComponent"] = type === "files" ? undefined : (e: any) => <CMSProjectInfo id={e.id} admin={!content} showBG />;
        const toRet: useCMSSearchProps<any, any> =
            content ?
                {
                    ...base,
                    dontUseRouteForState,
                    multiSelect: true,
                    placeholderText: type === "files" ? "search files" : "search projects",
                    columns,
                    searchData: sd,
                    getSuggestionsMethod: type === "files" ? CMSController.SuggestFiles : CMSController.SuggestProjects,
                    searchMethod: type === "files" ? CMSController.SearchFiles : CMSController.SearchProjects,
                    onRefineLoadedData: refine,
                    renderSubComponent,
                    onFileClicked,
                    onProjectClicked,
                    setLoadingPage,
                    onShowPreview: type === "files" ? setShowPreview : undefined,
                    renderSearchResults,
                    dontResetSearchResults,
                }
                :
                {
                    ...base,
                    dontUseRouteForState,
                    multiSelect: false,
                    placeholderText: type === "files" ? "search files" : "search projects",
                    columns,
                    searchData: sd,
                    searchMethod: type === "files" ? CMSController.SearchFiles : CMSController.SearchProjects,
                    getSuggestionsMethod: type === "files" ? CMSController.SuggestFiles : CMSController.SuggestProjects,
                    onRefineLoadedData: refine,
                    renderSubComponent,
                    onFileClicked,
                    onProjectClicked,
                    setLoadingPage,
                    onShowPreview: type === "files" ? setShowPreview : undefined,
                    renderSearchResults,
                    dontResetSearchResults,
                }
        return toRet;
    }, [content, type, setShowPreview, onFileClicked, onProjectClicked, setLoadingPage, dontUseRouteForState,
        renderSearchResults, dontResetSearchResults, ordered]);

    // React.useEffect(() => console.log("CMSSearch.tsx::207 **Changed** => content", content), [content]);
    // React.useEffect(() => console.log("CMSSearch.tsx::209 **Changed** => type", type), [type]);
    // React.useEffect(() => console.log("CMSSearch.tsx::210 **Changed** => setShowPreview"), [setShowPreview]);
    // React.useEffect(() => console.log("CMSSearch.tsx::210 **Changed** => toggleMarkedFileEntry"), [toggleMarkedFileEntry]);
    // React.useEffect(() => console.log("CMSSearch.tsx::210 **Changed** => toggleMarkedProject"), [toggleMarkedProject]);
    // React.useEffect(() => console.log("CMSSearch.tsx::210 **Changed** => sFBP"), [sFBP]);
    // React.useEffect(() => console.log("CMSSearch.tsx::210 **Changed** => sPBP"), [sPBP]);
    // React.useEffect(() => console.log("CMSSearch.tsx::210 **Changed** => callOnDownload"), [callOnDownload]);


    // React.useEffect(() => console.log("CMSSearch.tsx::208 **Changed** => props", props), [props]);
    const {
        SearchField,
        SearchResults,
        searchResults,
        loading,
        selection,
        updateState,
    } = useCMSSearch(props as any);

    //console.log("searchResults", searchResults);

    React.useEffect(() => {
        //console.log("check it!");
        if ((open.current || showPreview) && searchResults && (searchResults.totalCount ?? 0) > 0) {
            const f = _.find(searchResults.results, s => s.id === showPreview?.id);
            if (f === undefined) {
                const first = (searchResults?.results ?? [])[0];
                if ((first as any)?.path !== undefined)
                    setShowPreview(first as FileEntrySearchResultDto);
            }
        };
    }, [searchResults, showPreview]);

    React.useEffect(() => {
        //console.log("CMSSearch.tsx::189 => selection", selection);
        if (selection.length === 0)
            setPacks(undefined);
        else {
            if (type === "projects")
                setPacks(_.map(selection, s => ({ projectGroupId: s.id })));
            else {
                const toRet: CMSDownloadPack[] = [];
                _.forEach(selection, s => {
                    const sres = (s as FileEntrySearchResultDto);
                    let cur = _.find(toRet, t => t.projectGroupId === sres.projectGroupId);
                    if (cur === undefined) {
                        cur = {
                            projectGroupId: sres.projectGroupId,
                            fileEntryIds: []
                        };
                        toRet.push(cur);
                    }
                    cur.fileEntryIds?.push(sres.id);
                });
                setPacks(toRet);
            }
        }
    }, [selection, type]);

    React.useEffect(() => {
        if (onChangeLoading)
            onChangeLoading(loading);
    }, [loading, onChangeLoading])

    //const entriesFoundinGroups = searchResults?.projectGroups?.length ?? 0;
    //const entriesFoundinFiles = searchResults?.foundFileEntries?.length ?? 0;
    //const entriesFound = entriesFoundinGroups + entriesFoundinFiles;
    //console.log("CMSSearch.tsx::190 => disableSearchResults", disableSearchResults);

    const searchTypeIsFile = type === "files";

    const dispatchActions = React.useMemo(() => props.actions, [props.actions]);

    const toggleType = React.useMemo(() => () => {
        dispatch(dispatchActions.setSearchResults(undefined));
        const s = type === "files" ? (searchTerm === " " ? "" : searchTerm) : undefined;
        const t = type === "projects" ? "files" : "projects";
        updateState(s, undefined, undefined, undefined, undefined, t, undefined)
    }, [dispatch, dispatchActions, type, updateState, searchTerm]);


    const clearInput = React.useMemo(() => () => {
        const f = type === "files" ? " " : "";
        const t = type;
        updateState(f, { allowFuzzy: true }, {}, undefined, undefined, t, undefined);
        dispatch(dispatchActions.setSearchResults(undefined));
    }, [dispatch, dispatchActions, updateState, type]);


    const callDownload = React.useMemo(() => {
        const toRet = () => {
            if (packs && packs.length > 0) {
                if (onDownload)
                    onDownload(packs);
            }
        }
        return (packs ?? []).length > 0 ? toRet : undefined;
    }, [packs, onDownload]);

    const isSmallerView = contentWidth < 700;

    const closePreview = React.useMemo(() => () => {
        setShowPreview(undefined);
        const currentHash = window.location.hash;
        const hashEnd = currentHash.indexOf("?");
        if (hashEnd < 0)
            return;
        const hashPath = currentHash.substring(0, hashEnd);
        const hashQuery = currentHash.substring(hashEnd + 1);
        const query = new URLSearchParams(hashQuery);
        if (query.get("open") === "true") {
            query.delete("open");
            const nextQuery = query.toString();
            const nextHash = `${hashPath}${nextQuery.length > 0 ? `?${nextQuery}` : ""}`;
            window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
        }
    }, []);

    const navBackProps = React.useMemo(() => {
        return {
            onClick: () => {
                History.goBack();
            }
        };
    }, []);

    return (
        <div>
            <Loader size="small" active={loadingPage || givenLoading} light />
            {showPreview &&
                <Dimmer
                    active
                    onClick={closePreview}
                    button
                    leftButton={navBackProps}
                >
                    <>
                        <RenderFileEntryPreview
                            data={showPreview}
                            lng={lng}
                            location={location}
                        />
                    </>
                </Dimmer>

            }

            <Divider color="transparent" />

            <T.CategoryHeaderContainerHome isContent={content}>
                <T.CategorySearchContainerHomeTitle isMobile={isMobile}>
                    <Icon name={content ? "cms search" : "cms admin"} style={{ float: "left", marginRight: 10, marginLeft: isMobile ? 2 : -10 }} size={isMobile ? "1.1rem" : "1.3rem"} marginTop={isMobile ? 2 : 4} />
                    {/* {searchResults && (
                        routeData.ordered ?
                            (content ?
                                (type === "projects" ? t("{{count}} cms user view projects", { count: searchResults.totalCount ?? 0 }) : t("{{count}} cms user view files", { count: searchResults.totalCount ?? 0 })) :
                                (type === "projects" ? t("{{count}} cms admin view projects", { count: searchResults.totalCount ?? 0 }) : t("{{count}} cms admin view files", { count: searchResults.totalCount ?? 0 }))
                            ) :
                            (content ?
                                t("{{count}} cms user view search results", { count: searchResults.totalCount ?? 0 }) :
                                t("{{count}} cms admin view search results", { count: searchResults.totalCount ?? 0 }))
                    )
                    } */}
                    {searchResults && (
                        routeData.ordered ?
                            type === "projects" ? t("{{count}} projects", { count: searchResults.totalCount ?? 0 }) : t("{{count}} files", { count: searchResults.totalCount ?? 0 })
                            :
                            t("{{count}} search results", { count: searchResults.totalCount ?? 0 })
                    )
                    }


                    <T.CategorySearchContainerSwitcher active={true}>
                        {t("you are searching for {{type}}", { type: searchTypeIsFile ? "files" : "projects" })}
                    </T.CategorySearchContainerSwitcher>



                </T.CategorySearchContainerHomeTitle>
                <T.CategorySearchContainerHome style={{ left: isSmallerView ? "10px" : "23%", right: isSmallerView ? "10px" : "23%", width: "unset" }}>
                    <div style={{ width: "100%", display: "grid", height: 41, gridTemplateColumns: "1fr max-content max-content", }}>
                        <div style={{ position: "relative", top: -1 }}>
                            {SearchField}
                            {(searchTerm !== undefined && searchTerm !== " " && searchTerm !== "*") &&
                                <G.ResetSearchIcon onClick={clearInput} style={{ right: 0 }}>
                                    <Icon name="times" color={"@middleLightGrey"} />
                                </G.ResetSearchIcon>

                            }
                            <T.CategorySearchContainerSwitcherLeft
                                // onClick={searchTypeIsFile ? toggleType : undefined}
                                active={searchTypeIsFile}
                                style={{ right: 10 }}
                            >
                                <Tooltip tooltipText={t("cms search - input info")} noMargin notInline cursor='normal'>
                                    <Icon color="@middleLightGrey" name="info" />
                                </Tooltip>
                            </T.CategorySearchContainerSwitcherLeft>
                        </div>
                        <div style={{ paddingLeft: 5 }}><Button icon="file" kind={!searchTypeIsFile ? 'cancelButton' : "primary"} content="Files" tooltip={!searchTypeIsFile ? { tooltipText: t("search for files") } : undefined} onClick={searchTypeIsFile ? undefined : toggleType} /></div>
                        <div><Button icon="folder" kind={searchTypeIsFile ? 'cancelButton' : "primary"} content="Projects" tooltip={searchTypeIsFile ? { tooltipText: t("search for projects") } : undefined} onClick={searchTypeIsFile ? toggleType : undefined} /></div>
                    </div>
                </T.CategorySearchContainerHome>

            </T.CategoryHeaderContainerHome>
            <Divider color="transparent" />
            {renderHeader ? renderHeader(callDownload) : <div />}
            <LoaderInline active={loading} infoText={t("searching ...")} margin="0 0 20px 0" />
            <div style={{ display: loading ? "none" : "block" }}>
                <Provider context={useCMSSearchContext} store={useCMSSearchStore}>
                    <SearchResults isDisabled={disableSearchResults} />
                </Provider>
            </div>

        </div >
    );
}
export default CMSSearch;


